import { useEffect, useState } from 'react';
import { Download, ExternalLink, Loader2, Smartphone, X } from 'lucide-react';
import { deviceFsApi, filesApi, getToken } from '@/lib/api';
import { formatBytes, formatDuration, tsLocal } from '@/lib/format';
import type { FileMeta } from '@/lib/types';
import { runInstallExistingTask } from '@/lib/installRunner';
import clsx from 'clsx';

const IMAGE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);
const VIDEO_EXT = new Set(['mp4', 'm4v', 'webm', 'mov', 'mkv']);
const TEXT_EXT = new Set(['txt', 'log', 'json', 'xml', 'yml', 'yaml', 'md', 'html', 'csv', 'ini']);
const INSTALLABLE_EXT = new Set(['apk', 'xapk']);

function extOf(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? '';
}

function classifyExt(name: string): 'image' | 'video' | 'text' | 'other' {
  const ext = extOf(name);
  if (IMAGE_EXT.has(ext)) return 'image';
  if (VIDEO_EXT.has(ext)) return 'video';
  if (TEXT_EXT.has(ext)) return 'text';
  return 'other';
}

/**
 * Source descriptor for the preview. PC files come from devices (already
 * on disk); device files stream straight via `adb exec-out cat` so previewing
 * does NOT pollute the user's devices directory with copies they didn't ask
 * for. Only an explicit "download" / `transferApi.pull` should land bytes
 * on disk.
 */
export type PreviewSource =
  | { kind: 'pc'; meta: FileMeta }
  | {
      kind: 'device';
      serial: string;
      devicePath: string;
      name: string;
      /** Optional device-side size for header display. */
      size?: number;
    };

interface Props {
  source: PreviewSource;
  onClose: () => void;
  /** Optional caption shown next to the filename, e.g. 'PC' or 'Device'. */
  caption?: string;
  /**
   * Currently active device serial. Required to surface the "安装到手机"
   * action when previewing an APK / XAPK; without it we don't know
   * which device to target so the button stays hidden.
   */
  serial?: string;
  /** Active device's online status — gates the install button. */
  isOnline?: boolean;
  /** Human-readable device label for the install task card. */
  serialLabel?: string;
}

interface ResolvedPreview {
  name: string;
  bytes: number;
  mtime?: string;
  width?: number;
  height?: number;
  durationSec?: number;
  lines?: number;
  /** URL for inline <img>/<video> rendering (no download attribute). */
  inlineUrl: string;
  /** URL with download disposition (used by the download button). */
  downloadUrl: string;
  /** When set, TextPreview will fetch text from /api/files/text via this PC path. */
  pcPath?: string;
  /** When set, TextPreview will fetch text by streaming the device file. */
  deviceText?: { serial: string; devicePath: string };
}

function resolveSource(source: PreviewSource): ResolvedPreview {
  if (source.kind === 'pc') {
    const m = source.meta;
    return {
      name: m.name,
      bytes: m.bytes,
      mtime: m.mtime,
      width: m.extra?.width,
      height: m.extra?.height,
      durationSec: m.extra?.durationSec,
      lines: m.extra?.lines,
      inlineUrl: filesApi.rawUrl(m.path, false),
      downloadUrl: filesApi.rawUrl(m.path, true),
      pcPath: m.path,
    };
  }
  return {
    name: source.name,
    bytes: source.size ?? 0,
    inlineUrl: deviceFsApi.rawUrl(source.serial, source.devicePath, false),
    downloadUrl: deviceFsApi.rawUrl(source.serial, source.devicePath, true),
    deviceText: { serial: source.serial, devicePath: source.devicePath },
  };
}

/**
 * Unified preview modal. PC files render straight from /api/files/raw; device
 * files render from /api/devicefs/raw which streams `adb exec-out cat` —
 * no devices directory write happens until the user clicks "download".
 */
export function FilePreviewModal({
  source,
  onClose,
  caption,
  serial,
  isOnline,
  serialLabel,
}: Props) {
  const r = resolveSource(source);
  const kind = classifyExt(r.name);
  const ext = extOf(r.name);
  // Install action only makes sense for PC-side APK / XAPK files because
  // we hand the server path to `app.install` / `app.installXapk`
  // directly. Device-side previews would need a pull-then-install
  // detour that's out of scope for this surface.
  const isInstallable =
    source.kind === 'pc' && INSTALLABLE_EXT.has(ext) && (ext === 'apk' || ext === 'xapk');
  const canInstall = isInstallable && !!serial && !!isOnline;
  const [installToast, setInstallToast] = useState<string | undefined>();

  const onInstall = () => {
    if (!canInstall || source.kind !== 'pc') return;
    runInstallExistingTask({
      serverPath: source.meta.path,
      ext: ext as 'apk' | 'xapk',
      serial: serial!,
      fileName: source.meta.name,
      ...(serialLabel ? { serialLabel } : {}),
    });
    setInstallToast(`已加入安装队列：${source.meta.name}`);
    setTimeout(() => setInstallToast(undefined), 2500);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="card flex max-h-[90vh] w-[min(1000px,90vw)] flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-line bg-bg-soft px-3 py-2">
          <span className="font-mono text-sm text-ink">{r.name}</span>
          {caption && <span className="badge">{caption}</span>}
          {r.bytes > 0 && <span className="text-xs text-ink-muted">{formatBytes(r.bytes)}</span>}
          {r.mtime && <span className="text-xs text-ink-dim">{tsLocal(r.mtime)}</span>}
          {r.width && r.height && (
            <span className="text-xs text-ink-dim">
              {r.width}×{r.height}
            </span>
          )}
          {r.durationSec !== undefined && (
            <span className="text-xs text-ink-dim">{formatDuration(r.durationSec)}</span>
          )}
          {r.lines !== undefined && (
            <span className="text-xs text-ink-dim">{r.lines.toLocaleString()} 行</span>
          )}
          <div className="ml-auto flex items-center gap-1">
            {isInstallable && (
              <button
                className={clsx(
                  'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition',
                  canInstall
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20'
                    : 'cursor-not-allowed border-line bg-bg-soft text-ink-dim'
                )}
                onClick={onInstall}
                disabled={!canInstall}
                title={
                  canInstall
                    ? `安装到当前设备${serialLabel ? `（${serialLabel}）` : ''}`
                    : !serial
                      ? '没有选中设备'
                      : '设备离线，无法安装'
                }
              >
                <Smartphone className="h-3.5 w-3.5" />
                <span>安装到手机</span>
              </button>
            )}
            <a className="btn-ghost" href={r.downloadUrl} title="下载到 PC">
              <Download className="h-3.5 w-3.5" />
            </a>
            <a
              className="btn-ghost"
              href={r.inlineUrl}
              target="_blank"
              rel="noreferrer"
              title="新标签页打开"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button className="btn-ghost" onClick={onClose} title="关闭">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {installToast && (
          <div className="border-b border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-500">
            {installToast}（详情请见右下角安装队列）
          </div>
        )}
        {/*
          `min-h-0 overflow-auto` (no flex-1): body sizes to its natural
          content height, which keeps the modal compact when the image is
          short. The parent's `max-h-[90vh]` still provides the upper bound,
          and `overflow-auto` kicks in only when content exceeds it.
        */}
        <div className="min-h-0 overflow-auto bg-bg-soft">
          {kind === 'image' && (
            <img
              src={r.inlineUrl}
              alt={r.name}
              className="mx-auto block max-h-[80vh] w-auto"
            />
          )}
          {kind === 'video' && (
            <video
              src={r.inlineUrl}
              controls
              autoPlay
              className="mx-auto block max-h-[80vh] w-auto"
            />
          )}
          {kind === 'text' && r.pcPath && <TextPreview path={r.pcPath} />}
          {kind === 'text' && r.deviceText && (
            <DeviceTextPreview serial={r.deviceText.serial} devicePath={r.deviceText.devicePath} />
          )}
          {kind === 'other' && (
            <div className="p-6 text-center text-sm text-ink-muted">
              <p>暂不支持该类型预览。</p>
              <p className="mt-2">
                请点击右上角{isInstallable && '「安装到手机」、'}下载按钮或
                <a
                  className="ml-1 text-accent underline"
                  href={r.inlineUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  在新标签页打开
                </a>
                。
              </p>
              {isInstallable && !canInstall && (
                <p className="mt-2 text-xs text-amber-500">
                  {!serial ? '当前未选中任何设备。' : '当前设备离线，连上设备后即可安装。'}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Device-side text preview: streams up to 1 MB via /api/devicefs/raw,
 * splits client-side. Avoids landing the file in devices.
 */
function DeviceTextPreview({ serial, devicePath }: { serial: string; devicePath: string }) {
  const [lines, setLines] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | undefined>();
  const [wrap, setWrap] = useState(true);
  const MAX_BYTES = 1_048_576;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(undefined);
    setLines([]);

    const headers = new Headers();
    const token = getToken();
    if (token) headers.set('x-adh-token', token);
    fetch(deviceFsApi.rawUrl(serial, devicePath), { headers })
      .then(async (resp) => {
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}: ${(await resp.text()).slice(0, 200)}`);
        }
        const reader = resp.body?.getReader();
        if (!reader) {
          const text = await resp.text();
          return text.slice(0, MAX_BYTES);
        }
        const chunks: Uint8Array[] = [];
        let total = 0;
        while (total < MAX_BYTES) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            total += value.byteLength;
          }
        }
        try { reader.cancel(); } catch { /* ignore */ }
        return new TextDecoder('utf-8', { fatal: false }).decode(
          chunks.length === 1 ? chunks[0] : concatChunks(chunks)
        );
      })
      .then((text) => {
        if (cancelled) return;
        setLines(text.split(/\r?\n/));
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serial, devicePath]);

  return (
    <div className="space-y-2 p-3">
      <div className="flex items-center gap-2 text-[11px] text-ink-muted">
        <span>
          已加载 {lines.length.toLocaleString()} 行（设备直读，最大 1 MB）
        </span>
        <button className="btn-ghost ml-auto" onClick={() => setWrap((v) => !v)}>
          {wrap ? '换行' : '不换行'}
        </button>
      </div>
      <pre
        className={clsx(
          'rounded bg-bg-card p-2 font-mono text-[11px] text-ink',
          wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre overflow-auto'
        )}
      >
        {loading ? '加载中…' : lines.join('\n')}
      </pre>
      {err && <p className="text-xs text-rose-500">{err}</p>}
    </div>
  );
}

function concatChunks(chunks: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const c of chunks) total += c.byteLength;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

function TextPreview({ path }: { path: string }) {
  const [lines, setLines] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | undefined>();
  const [reachedEnd, setReachedEnd] = useState(false);
  const [offset, setOffset] = useState(0);
  const PAGE = 500;
  const [wrap, setWrap] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(undefined);
    setLines([]);
    setOffset(0);
    setReachedEnd(false);
    filesApi
      .text({ path, offset: 0, limit: PAGE })
      .then((r) => {
        if (cancelled) return;
        setLines(r.lines);
        setOffset(r.offset + r.lines.length);
        setReachedEnd(r.reachedEnd);
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const loadMore = async () => {
    setLoading(true);
    try {
      const r = await filesApi.text({ path, offset, limit: PAGE });
      setLines((prev) => prev.concat(r.lines));
      setOffset(r.offset + r.lines.length);
      setReachedEnd(r.reachedEnd);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2 p-3">
      <div className="flex items-center gap-2 text-[11px] text-ink-muted">
        <span>
          已加载 {lines.length.toLocaleString()} 行
          {reachedEnd && ' · 已到末尾'}
        </span>
        <button className="btn-ghost ml-auto" onClick={() => setWrap((v) => !v)}>
          {wrap ? '换行' : '不换行'}
        </button>
      </div>
      <pre
        className={clsx(
          'rounded bg-bg-card p-2 font-mono text-[11px] text-ink',
          wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre overflow-auto'
        )}
      >
        {lines.join('\n')}
      </pre>
      {err && <p className="text-xs text-rose-500">{err}</p>}
      <div className="flex justify-center">
        {!reachedEnd && (
          <button className="btn" disabled={loading} onClick={loadMore}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            <span>加载更多</span>
          </button>
        )}
      </div>
    </div>
  );
}
